/**
 * The operating-expense mix: each expense line as a share of the total.
 *
 * The one card on the Charts tab that is not a trend. Summit's analyst asked
 * for expenses as percentages of the whole — where the money goes, not how each
 * line moved — which twelve dollar lines on one time axis never showed.
 *
 * The legend carries the numbers (share, then dollars), largest first, so the
 * ring only has to show proportion. Same numbers as the Table, summed over the
 * periods named under the title.
 */

import ReactEChartsCore from "echarts-for-react/lib/core";
import { PieChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import React, { useMemo } from "react";

import { formatKpiValue, formatPeriod } from "./formatKpiValue";
import type { KpiExpenseMix } from "./kpiCharts";

echarts.use([PieChart, TooltipComponent, CanvasRenderer]);

/*
 * One color per expense line the pack can carry, plus the unitemized
 * remainder. Hue varies first, as on the trend cards; neighbours in the ring
 * are sorted by size, so adjacent slices are adjacent entries here.
 */
const SLICE_COLORS = [
	"#004C4B",
	"#C2410C",
	"#3538CD",
	"#CA8A04",
	"#9333EA",
	"#0E9384",
	"#BE185D",
	"#5B6472",
	"#1D4ED8",
	"#65A30D",
	"#7C2D12",
	"#94A3B8",
];

/** Module constant: an inline object literal would remount the chart. */
const CHART_STYLE = { height: 190, width: 190 };

export interface KpiMixCardProps {
	label: string;
	mix: KpiExpenseMix;
	/** The table's grain — names the periods the totals cover. */
	grain?: string;
}

function formatShare(share: number): string {
	return `${(share * 100).toFixed(1)}%`;
}

const KpiMixCard: React.FC<KpiMixCardProps> = ({ label, mix, grain = "month" }) => {
	const { slices, periods, total, credits } = mix;
	const first = formatPeriod(periods[0], grain);
	const last = formatPeriod(periods[periods.length - 1], grain);
	const span = first === last ? first : `${first} – ${last}`;

	const option = useMemo(
		() => ({
			tooltip: {
				trigger: "item" as const,
				backgroundColor: "#111827",
				borderWidth: 0,
				padding: [9, 11],
				textStyle: { color: "#ffffff", fontSize: 13 },
				extraCssText:
					"border-radius:8px;box-shadow:0 12px 28px rgba(17,24,39,0.28);",
				formatter: (params: unknown) => {
					const item = params as { dataIndex?: number };
					const slice = slices[item.dataIndex ?? 0];
					if (!slice) return "";
					// Labels are the pack's own names or this file's — no markup in them.
					return `<div style="font-size:12px;color:#d1d5db">${slice.label}</div><div style="font-weight:600">${formatShare(
						slice.share,
					)} · ${formatKpiValue(slice.value, "usd")}</div>`;
				},
			},
			series: [
				{
					type: "pie" as const,
					radius: ["52%", "88%"],
					// Largest slice starts at twelve o'clock and they run clockwise,
					// the order the legend lists them in.
					startAngle: 90,
					clockwise: true,
					avoidLabelOverlap: false,
					label: { show: false },
					labelLine: { show: false },
					itemStyle: { borderColor: "#ffffff", borderWidth: 1.5 },
					emphasis: { scaleSize: 4 },
					cursor: "default",
					data: slices.map((slice, index) => ({
						name: slice.label,
						value: slice.value,
						itemStyle: { color: SLICE_COLORS[index % SLICE_COLORS.length] },
					})),
				},
			],
		}),
		[slices],
	);

	return (
		<section
			aria-label={label}
			className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white px-4 pb-2.5 pt-3.5"
		>
			<div className="flex min-w-0 flex-col gap-0.5">
				<h3 className="text-sm font-semibold text-text-strong">{label}</h3>
				<div className="flex flex-wrap items-baseline gap-x-2">
					<span className="text-[22px] font-bold tabular-nums text-[#111111]">
						{formatKpiValue(total, "usd")}
					</span>
					<span className="text-xs text-text-subtle">
						Total operating expenses, {span}
					</span>
				</div>
			</div>
			<div className="flex flex-wrap items-center gap-x-6 gap-y-2">
				<ReactEChartsCore
					echarts={echarts}
					option={option}
					style={CHART_STYLE}
					notMerge
					lazyUpdate
				/>
				<ul className="flex min-w-[220px] flex-1 flex-col gap-1 text-xs text-text-muted">
					{slices.map((slice, index) => (
						<li key={slice.key} className="flex items-center gap-2">
							<span
								aria-hidden="true"
								className="h-2.5 w-2.5 shrink-0 rounded-sm"
								style={{ background: SLICE_COLORS[index % SLICE_COLORS.length] }}
							/>
							<span className="min-w-0 flex-1 truncate">{slice.label}</span>
							<span className="font-semibold tabular-nums text-text-strong">
								{formatShare(slice.share)}
							</span>
							<span className="w-20 text-right tabular-nums">
								{formatKpiValue(slice.value, "usd")}
							</span>
						</li>
					))}
				</ul>
			</div>
			{credits.length > 0 && (
				<p className="text-xs text-text-muted">
					Shares are of expenses before credits. Not drawn (net credit):{" "}
					{credits
						.map((line) => `${line.label} ${formatKpiValue(line.value, "usd")}`)
						.join(", ")}
				</p>
			)}
		</section>
	);
};

export default KpiMixCard;
